import { View } from 'react-native';

import { Surface } from '@/components/ui/Surface';
import { Text } from '@/components/ui/Text';

export interface SealedRunSummaryProps {
  done: number;
  total: number;
  /** e.g. "paulstraw" or "JS & MK" */
  sealedBy?: string;
  /** e.g. "Jul 4, 2026 · 3:21 PM" */
  sealedAt: string;
  /**
   * When the seal reached the server, e.g. "Jul 4, 2026 · 7:05 PM". Set ONLY when that
   * meaningfully lags `sealedAt` (see `delayedSealUpload`) — the seal did not go up
   * with the run. Its presence is the signal; leave it unset for an ordinary online
   * seal.
   */
  uploadedAt?: string;
}

/** The at-a-glance summary card atop a sealed run: completion stat + who/when. */
export function SealedRunSummary({
  done,
  total,
  sealedBy,
  sealedAt,
  uploadedAt,
}: SealedRunSummaryProps) {
  return (
    <Surface variant="card" className="flex-row items-center gap-4 p-4">
      <View>
        <Text variant="display" className="text-3xl leading-none">
          {done}
          <Text variant="none" className="font-heading text-lg text-muted">
            /{total}
          </Text>
        </Text>
        <Text variant="accent" className="mt-1">
          steps completed
        </Text>
      </View>

      <View className="w-px self-stretch bg-border" />

      <View className="flex-1">
        <Text variant="body" className="text-foreground-secondary">
          Sealed by{' '}
          <Text variant="none" className="font-sans-semibold text-foreground">
            {sealedBy ?? 'Unknown'}
          </Text>
        </Text>
        <Text variant="body" className="mt-0.5 text-foreground-secondary">
          {sealedAt}
        </Text>
        {/* Subordinate to the seal time on purpose: the authored instant is the fact
            of record, this is only its provenance. The line states just the timestamp:
            the data can't tell an offline seal from one whose upload merely lagged, so
            it claims nothing about why. */}
        {uploadedAt ? (
          <Text variant="caption" className="mt-0.5">
            Uploaded {uploadedAt}
          </Text>
        ) : null}
      </View>
    </Surface>
  );
}
